import {
    type ComponentChildren,
    createContext,
    type FunctionComponent,
    isValidElement,
    toChildArray,
} from "preact";
import * as stylex from "@stylexjs/stylex";
import { useContext, useState } from "preact/hooks";

interface TabDescriptor {
    target: string;
    label: string;
}

type TabProps = Omit<TabDescriptor, "target"> & {
    active?: boolean;
    onClick: () => void;
};

interface TabContainerProps {
    tabs: TabDescriptor[];
    extras?: ComponentChildren;
    onTabChange?: (tab: string) => Promise<boolean> | boolean;
}

const tabColor = "#039ACE";
const styles = stylex.create({
    container: {
        gap: "10px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    tabs: {
        gap: "10px",
        display: "flex",
        position: "relative",
        paddingTop: "9px",
        paddingLeft: "10px",
        borderWidth: "4px",
        borderColor: tabColor,
        borderBottomStyle: "solid",
        backgroundColor: "#FAFAFA",
        fontFamily: "'Inter Variable', sans-serif",
    },
    extras: {
        display: "flex",
        color: "#444",
        fontSize: "14px",
        alignItems: "center",
        position: "absolute",
        height: "100%",
        right: 5,
        top: 0,
    },
    tab: {
        appearance: "none",
        borderWidth: "0",
        borderTopRightRadius: "6px",
        borderTopLeftRadius: "6px",
        backgroundColor: "#EEE",
        color: `color-mix(in prophoto-rgb, #EEE 35%, #000 65%)`,
        padding: "8px 10px",
        cursor: "pointer",
        fontFamily: "'Inter Variable', sans-serif",
    },
    tabActive: {
        backgroundColor: tabColor,
        color: "#FFF",
    },
    tabWrapper: {
        maxHeight: "calc(100% - 45px)",
        overflowY: "auto",
    },
});

const Tab: FunctionComponent<TabProps> = ({ label, onClick, active = false }) => {
    const styleProps: any = [styles.tab];
    if (active) styleProps.push(styles.tabActive);

    return (
        <button {...stylex.props(...styleProps)} onClick={onClick}>
            {label}
        </button>
    );
};

const TabContext = createContext<{
    activeTab: string;
    setActiveTab: (tab: string) => void;
} | null>(null);

const TabHeader: FunctionComponent<TabContainerProps> = ({
    tabs,
    extras,
    onTabChange = (_) => true,
}) => {
    const ctx = useContext(TabContext);
    if (!ctx) throw new Error("TabHeader should be used only within TabContainer");

    const trySettingTab = async (tab: string) => {
        if (await onTabChange(tab)) ctx.setActiveTab(tab);
    };

    return (
        <div {...stylex.props(styles.tabs)}>
            {tabs.map(({ target, label }) => (
                <Tab
                    active={ctx.activeTab == target}
                    label={label}
                    onClick={() => trySettingTab(target)}
                />
            ))}
            {!extras ? null : <div {...stylex.props(styles.extras)}>{extras}</div>}
        </div>
    );
};

const TabContainer: FunctionComponent<TabContainerProps> = (props) => {
    const [activeTab, setActiveTab] = useState<string>(props.tabs[0].target);

    if (typeof props.children != "object" || !(props.children instanceof Array))
        throw new Error("TabContainer must contain children");

    return (
        <TabContext.Provider value={{ activeTab, setActiveTab }}>
            <div {...stylex.props(styles.container)}>
                <TabHeader {...props} />
                <>
                    {toChildArray(props.children)
                        .filter((child) => isValidElement(child) && child.type == TabContent)
                        .map((child) => (
                            <div {...stylex.props(styles.tabWrapper)}>{child}</div>
                        ))}
                </>
            </div>
        </TabContext.Provider>
    );
};

const TabContent: FunctionComponent<{ name: string }> = ({ name, children }) => {
    const ctx = useContext(TabContext);
    if (!ctx) throw new Error("TabContent should be used only within TabContainer");

    return name == ctx.activeTab ? <div data-tab-name={name}>{children}</div> : null;
};

export default {
    Container: TabContainer,
    Content: TabContent,
};
